import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import ReactAvatarEditor from 'react-avatar-editor';
import { Box, Button, Slider, Typography, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';

class ImageCrop extends Component {
  constructor(props) {
    super(props);
    this.state = {
      image: props ? props.imageUrl : null,
      scale: 1,
      imageChanged: false,
      isDragging: false,
      deleteDialogOpen: false
    };
    this.fileInputRef = React.createRef();
  }

  componentDidMount() {
    if (this.props.imageUrl) {
      this.setState({ image: this.props.imageUrl });
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.imageUrl !== prevProps.imageUrl && !this.state.imageChanged) {
      this.setState({ image: this.props.imageUrl || null, scale: 1, imageChanged: false });
    }
  }

  getImageFileAndSave(saveFunc) {
    if (this.editor) {
      const canvasScaledBase64 = this.editor.getImageScaledToCanvas().toDataURL();
      fetch(canvasScaledBase64)
        .then(res => res.blob())
        .then(blob => {
          const file = new File([blob], `${this.props.imageSaveName || 'image'}.png`);
          return saveFunc(file).then(() => {
            this.setState({ imageChanged: false });
          });
        })
        .catch(err => console.error('Error in getImageFileAndSave:', err));
    }
  }

  handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      this.setState({ image: e.target.files[0], imageChanged: true, scale: 1 });
    }
  };

  handleImageChange = () => {
    !this.state.imageChanged && this.setState({ imageChanged: true });
  };

  handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ isDragging: true });
  };

  handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ isDragging: false });
  };

  handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ isDragging: false });
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.type.startsWith('image/')) {
        this.setState({ image: file });
      }
    }
  };

  handleBoxClick = () => {
    this.fileInputRef.current?.click();
  };

  setEditorRef = (editor) => this.editor = editor;

  render() {
    const { editorWidth = 800, editorHeight = 400, acceptedFormats = 'image/*' } = this.props;
    const { image, scale, isDragging } = this.state;

    return (
      <Box sx={{ width: '100%' }}>
        <input
          ref={this.fileInputRef}
          type="file"
          accept={acceptedFormats}
          style={{ display: 'none' }}
          onChange={this.handleFileSelect}
        />
        
        <Box
          onDragOver={this.handleDragOver}
          onDragLeave={this.handleDragLeave}
          onDrop={this.handleDrop}
          onDoubleClick={this.handleBoxClick}
          sx={{
            width: '100%',
            height: editorHeight,
            border: image ? '1px solid #b0d4f1' : '2px dashed #ccc',
            borderRadius: 2,
            backgroundColor: isDragging ? '#e3f2fd' : 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'background-color 0.2s',
            overflow: 'hidden'
          }}
        >
          {image ? (
            <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ReactAvatarEditor
                ref={this.setEditorRef}
                crossOrigin="anonymous"
                image={image}
                width={editorWidth}
                height={editorHeight}
                border={[0, 0]}
                borderRadius={0}
                scale={scale}
                onLoadSuccess={() => !this.props.imageUrl && this.handleImageChange()}
                onImageChange={_.debounce(this.handleImageChange, 500)}
                style={{ width: '100%', height: '100%', display: 'block' }}
              />
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Double click to upload
            </Typography>
          )}
        </Box>

        {image && (
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ minWidth: 50 }}>Zoom:</Typography>
            <Slider
              value={scale}
              onChange={(e, value) => {
                this.setState({ scale: value });
                this.handleImageChange();
              }}
              min={0.5}
              max={5}
              step={0.01}
              sx={{ flex: 1 }}
            />
          </Box>
        )}

        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            onClick={() => this.fileInputRef.current?.click()}
          >
            {image ? 'Change image' : 'Add image'}
          </Button>
          {image && this.state.imageChanged && this.props.saveImageFunction && (
            <Button
              variant="contained"
              color="success"
              size="small"
              onClick={() => this.getImageFileAndSave(this.props.saveImageFunction)}
            >
              Save
            </Button>
          )}
          {image && this.props.imageUrl && this.props.deleteImageFunction && (
            <Button
              variant="contained"
              color="error"
              size="small"
              onClick={() => this.setState({ deleteDialogOpen: true })}
            >
              Delete image
            </Button>
          )}
        </Box>
        
        <Dialog
          open={this.state.deleteDialogOpen}
          onClose={() => this.setState({ deleteDialogOpen: false })}
        >
          <DialogTitle>Delete Image</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete this image? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => this.setState({ deleteDialogOpen: false })}>Cancel</Button>
            <Button 
              onClick={() => {
                this.setState({ deleteDialogOpen: false });
                this.props.deleteImageFunction();
              }} 
              color="error" 
              variant="contained"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }
}

ImageCrop.propTypes = {
  imageUrl: PropTypes.string,
  imageSaveName: PropTypes.string,
  saveImageFunction: PropTypes.func,
  deleteImageFunction: PropTypes.func,
  editorHeight: PropTypes.number,
  editorWidth: PropTypes.number,
  acceptedFormats: PropTypes.string
};

export default ImageCrop;
